import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn, MainCtaScope } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import TrustReconciliation from "./TrustReconciliation";
import TrustShortfalls from "./TrustShortfalls";
import TrustProperty from "./TrustProperty";
import TrustTradeRecord from "./TrustTradeRecord";
import TrustUnclaimed from "./TrustUnclaimed";
import TrustInterest from "./TrustInterest";
import TrustYearEnd from "./TrustYearEnd";

// Ontario trust ledger: one Real Estate Trust Account, broker-of-record approval,
// per-deal balances, receipt vs bank-deposit dates (5 business days), reversals only.
const KINDS = [
  ['deposit_received', 'Deposit received', 'in'],
  ['interest_earned', 'Interest earned', 'in'],
  ['brokerage_top_up', 'Brokerage top-up (shortfall)', 'in'],
  ['release_to_seller', 'Release to seller', 'out'],
  ['refund_to_buyer', 'Refund to buyer', 'out'],
  ['payment_to_coop', 'Payment to co-operating brokerage', 'out'],
  ['commission_to_general', 'Earned commission to general account', 'out'],
  ['interest_to_beneficiary', 'Interest paid to beneficiary', 'out'],
  ['unclaimed_to_reco', 'Unclaimed money paid to RECO', 'out'],
];
const KIND_LABEL = Object.fromEntries(KINDS.map(([k, l]) => [k, l]));
KIND_LABEL.reversal = 'Reversal';
const STATUS_TONE = { pending:'warn', approved:'ok', rejected:'neutral' };

const cad = (cents) => new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const centsOf = (e) => e.amount_cents ?? Math.round((e.amount || 0) * 100);
const dirOf = (e) => e.direction || (e.type === 'deposit' ? 'in' : 'out');
const day = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' }) : '—';

const EMPTY = { kind:'deposit_received', amount:'', transaction_id:'', payer:'', on_behalf_of:'', receiving_registrant:'', received_date:'', deposited_date:'', payee:'', purpose:'', reference_number:'', date:'' };

export default function TrustLedger({ BASE, headers, p, dark, entries, onChange }) {
  const [ov, setOv] = useState(null);
  const [deals, setDeals] = useState([]);
  const [showEntry, setShowEntry] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [acct, setAcct] = useState({});
  const [busy, setBusy] = useState(false);

  const loadOv = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/overview`, { headers }).then(r => r.ok ? r.json() : null).then(setOv).catch(() => setOv(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => {
    loadOv();
    fetch(`${BASE}/api/transactions`, { headers }).then(r => r.json()).then(d => setDeals(Array.isArray(d) ? d : [])).catch(() => {});
  }, [loadOv]); // eslint-disable-line

  const refresh = () => { loadOv(); onChange && onChange(); };
  const call = async (path, body, ok) => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/trust${path}`, { method: path === '/account' ? 'PUT' : 'POST', headers, body: JSON.stringify(body || {}) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      toast.success(ok);
      refresh();
      return d;
    } catch (e) { toast.error(e.message); return null; }
    finally { setBusy(false); }
  };

  const account = ov?.account;
  const members = ov?.members || [];
  const nameOf = (id) => members.find(m => m.id === id)?.name || '—';
  const canPost = !!ov?.can_post;
  const canAuth = !!ov?.can_authorize;
  const kindDir = (KINDS.find(k => k[0] === form.kind) || [])[2];
  const overdue = new Set(ov?.overdue_deposits || []);

  const input = { marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const field = (label, key, extra = {}) => (
    <div><Over color={p.ink3}>{label}</Over>
      <input value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ ...input, ...(extra.mono ? { fontFamily:F.mono } : {}) }} type={extra.type || 'text'} placeholder={extra.placeholder || ''}/>
    </div>
  );
  const small = (color) => ({ background:'none', border:`1px solid ${p.hair}`, borderRadius:2, padding:'4px 10px', cursor:'pointer', fontSize:11, fontFamily:F.mono, color });

  const openEntry = () => {
    const today = ov?.today || new Date().toISOString().slice(0, 10);
    setForm({ ...EMPTY, received_date: today, date: today });
    setShowEntry(true);
  };
  const submitEntry = async () => {
    const body = { ...form };
    Object.keys(body).forEach(k => { if (body[k] === '') delete body[k]; });
    const d = await call('', body, 'Entry recorded. It counts once the broker of record approves it.');
    if (d) setShowEntry(false);
  };
  const openAccount = () => {
    setAcct({
      institution: account?.institution || '', transit_number: account?.transit_number || '', account_last4: account?.account_last4 || '',
      opened_on: account?.opened_on || '', interest_bearing: !!account?.interest_bearing, interest_rate: account?.interest_rate ?? '', variable_rate: !!account?.variable_rate,
      broker_of_record_id: account?.broker_of_record_id || '', alternate_broker_id: account?.alternate_broker_id || '',
    });
    setShowAccount(true);
  };
  const saveAccount = async () => {
    const d = await call('/account', { ...acct, interest_rate: acct.interest_rate === '' ? null : String(acct.interest_rate) }, 'Trust account saved');
    if (d) setShowAccount(false);
  };
  const exportCsv = async () => {
    const res = await fetch(`${BASE}/api/accounting/trust/export.csv`, { headers });
    if (!res.ok) return toast.error('Export failed');
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `trust-ledger-${ov?.today || 'export'}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const pending = entries.filter(e => e.status === 'pending');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Balance + actions */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <Over color={p.ink3}>Approved trust balance</Over>
          <div style={{ fontFamily:F.mono, fontSize:24, letterSpacing:-1, marginTop:6, color:(ov?.balance_cents || 0) >= 0 ? T.ok : T.bad }}>{cad(ov?.balance_cents)}</div>
          <div style={{ fontSize:12, color:p.ink3, marginTop:6 }}>
            {ov?.pending_count ? `${ov.pending_count} awaiting broker-of-record approval · ` : ''}
            {overdue.size ? <span style={{ color:T.bad }}>{overdue.size} deposit{overdue.size === 1 ? '' : 's'} past the 5-business-day limit</span> : 'No late deposits'}
          </div>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {(canPost || canAuth) && <Btn variant="ghost" dark={dark} onClick={exportCsv}>Export CSV</Btn>}
          <MainCtaScope>
            {canPost && <Btn variant={account?.broker_of_record_id ? "ghost" : "primary"} dark={dark} onClick={openAccount}>{account?.broker_of_record_id ? "Account settings" : "Set up trust account"}</Btn>}
            {canPost && account?.broker_of_record_id && <Btn variant="primary" dark={dark} icon="plus" onClick={openEntry}>New entry</Btn>}
          </MainCtaScope>
        </div>
      </div>

      {/* Account card */}
      <div style={{ border:`1px solid ${p.hair}`, borderRadius:2, padding:'16px var(--bx-pad)', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16 }}>
        {account ? (<>
          <div><Over color={p.ink3}>Account</Over><div style={{ fontSize:13, marginTop:6 }}>{account.designated_name || 'Real Estate Trust Account'}</div><div style={{ fontSize:12, color:p.ink3, marginTop:2 }}>{account.institution || 'Institution not set'}{account.account_last4 ? ` · ••${account.account_last4}` : ''}</div></div>
          <div><Over color={p.ink3}>Broker of record</Over><div style={{ fontSize:13, marginTop:6 }}>{nameOf(account.broker_of_record_id)}</div></div>
          <div><Over color={p.ink3}>Alternate</Over><div style={{ fontSize:13, marginTop:6 }}>{account.alternate_broker_id ? nameOf(account.alternate_broker_id) : 'None named'}</div></div>
          <div><Over color={p.ink3}>Interest</Over><div style={{ fontSize:13, marginTop:6 }}>{account.interest_bearing ? `Interest-bearing${account.interest_rate != null ? ` · ${account.interest_rate}%` : ''}${account.variable_rate ? ' variable' : ''}` : 'Non-interest-bearing'}</div></div>
        </>) : (
          <div style={{ fontSize:13, color:p.ink2 }}>
            Set up the office’s Real Estate Trust Account and name the broker of record first. Every trust entry waits for their approval before it counts.
          </div>
        )}
      </div>

      {/* Approvals */}
      {canAuth && pending.length > 0 && (
        <div style={{ border:`1px solid ${T.warn}`, borderRadius:2 }}>
          <div style={{ padding:'12px var(--bx-pad)', borderBottom:`1px solid ${p.hair}` }}><Over color={T.warn}>Awaiting your approval</Over></div>
          {pending.map((e, i) => (
            <div key={e.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'12px var(--bx-pad)', borderBottom:i === pending.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize:13 }}>
              <div style={{ minWidth:0 }}>
                <div>{KIND_LABEL[e.kind] || e.type} · {e.property_address || 'No deal'}</div>
                <div style={{ fontSize:12, color:p.ink3, marginTop:2 }}>{dirOf(e) === 'in' ? `From ${e.payer || '—'}` : `To ${e.payee || '—'}`}{e.purpose ? ` · ${e.purpose}` : ''}</div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                <span style={{ fontFamily:F.mono, color: dirOf(e) === 'in' ? T.ok : T.bad }}>{dirOf(e) === 'in' ? '+' : '−'}{cad(centsOf(e))}</span>
                <button disabled={busy} style={small(T.ok)} onClick={() => call(`/${e.id}/approve`, {}, 'Approved')}>Approve</button>
                <button disabled={busy} style={small(p.ink3)} onClick={() => { const reason = window.prompt('Why are you rejecting this entry?'); if (reason) call(`/${e.id}/reject`, { reason }, 'Rejected'); }}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Per-deal balances */}
      {(ov?.trades || []).length > 0 && (
        <div style={{ border:`1px solid ${p.hair}`, borderRadius:2 }}>
          <div style={{ padding:'12px var(--bx-pad)', borderBottom:`1px solid ${p.hair}` }}><Over color={p.ink3}>Held in trust by deal</Over></div>
          {ov.trades.map((t, i) => (
            <div key={t.transaction_id || 'none'} style={{ display:'flex', justifyContent:'space-between', padding:'10px var(--bx-pad)', borderBottom:i === ov.trades.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize:13 }}>
              <span style={{ color:p.ink2 }}>{t.property_address || 'Deal'}</span>
              <span style={{ fontFamily:F.mono, color: t.balance_cents < 0 ? T.bad : p.ink }}>{cad(t.balance_cents)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Monthly reconciliation (579/05 s.13) */}
      {account && (canPost || canAuth) && <TrustReconciliation BASE={BASE} headers={headers} p={p} dark={dark} canPrepare={canPost || canAuth} canSign={canAuth}/>}

      {/* Shortfall notification log (579/05 s.14) */}
      {account && (canPost || canAuth) && <TrustShortfalls BASE={BASE} headers={headers} p={p} dark={dark} canManage={canAuth || canPost}/>}

      {/* Non-cash property in trust (567/05 s.20; 579/05 ss.15–16) */}
      {account && (canPost || canAuth) && <TrustProperty BASE={BASE} headers={headers} p={p} dark={dark} canManage={canAuth || canPost} canAuth={canAuth}/>}

      {/* Trade record sheets (579/05 s.17) */}
      {account && (canPost || canAuth) && <TrustTradeRecord BASE={BASE} headers={headers} p={p} dark={dark} deals={deals} canAuth={canAuth}/>}

      {/* Deposit-terms disclosure and interest (TRESA s.27(2)-(3)) */}
      {account && (canPost || canAuth) && <TrustInterest BASE={BASE} headers={headers} p={p} dark={dark}/>}

      {/* Unclear / unclaimed money two-year aging (TRESA s.27(4)-(7)) */}
      {account && (canPost || canAuth) && <TrustUnclaimed BASE={BASE} headers={headers} p={p} dark={dark} canAuth={canAuth}/>}

      {/* Year-end filing pack (RECO annual financial filing) + 6-year retention export (579/05 s.19) */}
      {account && (canPost || canAuth) && <TrustYearEnd BASE={BASE} headers={headers} p={p} dark={dark}/>}

      {/* Ledger */}
      <div className="overflow-x-auto">
        <div style={{ border:`1px solid ${p.hair}`, borderRadius:2, minWidth:820 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1.6fr 1.6fr 1.2fr 1fr 0.9fr auto', gap:12, padding:'10px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, fontSize: 12, fontWeight: 500, color:p.ink4 }}>
            <div>Date</div><div>Entry</div><div>Deal · party</div><div>Bank deposit</div><div style={{ textAlign:'right' }}>Amount</div><div>Status</div><div/>
          </div>
          {entries.length === 0 && <div style={{ padding:'48px var(--bx-pad)', textAlign:'center', fontSize:13, color:p.ink3 }}>No trust entries yet.</div>}
          {entries.map((e, i) => {
            const dep = e.deposit;
            const late = overdue.has(e.id) || dep?.late;
            return (
              <div key={e.id} style={{ display:'grid', gridTemplateColumns:'1fr 1.6fr 1.6fr 1.2fr 1fr 0.9fr auto', gap:12, padding:'12px var(--bx-pad)', borderBottom:i === entries.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize:13, alignItems:'center', opacity: e.status === 'rejected' ? 0.55 : 1 }}>
                <div style={{ fontFamily:F.mono, fontSize:12, color:p.ink3 }}>{day(e.received_date || e.date || e.created_at)}</div>
                <div>
                  <div>{KIND_LABEL[e.kind] || e.type}</div>
                  <div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{e.purpose || e.description}{e.authorized_by_name ? ` · ${e.status === 'rejected' ? 'rejected' : 'approved'} by ${e.authorized_by_name}` : ''}</div>
                </div>
                <div style={{ color:p.ink2 }}>
                  <div>{e.property_address || '—'}</div>
                  <div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{dirOf(e) === 'in' ? (e.payer ? `From ${e.payer}` : '') : (e.payee ? `To ${e.payee}` : '')}</div>
                </div>
                <div style={{ fontFamily:F.mono, fontSize:12, color: late ? T.bad : p.ink3 }}>
                  {dep ? (dep.deposited ? `${day(dep.deposited)}${dep.late ? ' · late' : ''}` : `Due ${day(dep.due)}${dep.overdue ? ' · overdue' : ''}`) : '—'}
                </div>
                <div style={{ textAlign:'right', fontFamily:F.mono, color: dirOf(e) === 'in' ? T.ok : T.bad }}>{dirOf(e) === 'in' ? '+' : '−'}{cad(centsOf(e))}</div>
                <div><Pill tone={STATUS_TONE[e.status] || 'neutral'} dark={dark}>{e.status || 'approved'}</Pill></div>
                <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                  {canPost && dep && !dep.deposited && e.status !== 'rejected' && (
                    <button disabled={busy} style={small(T.brick)} onClick={() => { const d = window.prompt('Date deposited at the bank (YYYY-MM-DD)', ov?.today || ''); if (d) call(`/${e.id}/deposit`, { deposited_date: d }, 'Deposit date recorded'); }}>Deposited</button>
                  )}
                  {canPost && e.status === 'approved' && e.kind !== 'reversal' && (
                    <button disabled={busy} style={small(p.ink3)} onClick={() => { const reason = window.prompt('Why reverse this entry? A matching opposite entry goes to the broker of record for approval.'); if (reason) call(`/${e.id}/reverse`, { reason }, 'Reversal sent for approval'); }}>Reverse</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* New entry */}
      <Dialog open={showEntry} onOpenChange={setShowEntry}>
        <DialogContent style={{ maxWidth:520, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)', maxHeight:'90vh', overflowY:'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>New trust entry</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>It stays pending until the broker of record approves it. Entries can’t be edited afterwards, only reversed.</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:14 }}>
              <div><Over color={p.ink3}>Type</Over>
                <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} style={input}>
                  {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              {field('Amount (CAD)', 'amount', { type:'number', mono:true, placeholder:'50000' })}
            </div>
            <div><Over color={p.ink3}>Deal{form.kind === 'brokerage_top_up' ? ' (optional)' : ''}</Over>
              <select value={form.transaction_id} onChange={e => setForm({ ...form, transaction_id: e.target.value })} style={input}>
                <option value="">{form.kind === 'brokerage_top_up' ? 'No deal (office top-up)' : 'Pick the deal'}</option>
                {deals.map(d => <option key={d.id} value={d.id}>{d.property_address}{d.mls_number ? ` · ${d.mls_number}` : ''}</option>)}
              </select>
            </div>
            {kindDir === 'in' ? (<>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {field('Paid by', 'payer', { placeholder:'Buyer name' })}
                {field('On behalf of', 'on_behalf_of', { placeholder:'Buyer name' })}
              </div>
              {field('Received by (registrant)', 'receiving_registrant', { placeholder:'Agent who took the deposit' })}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {field('Date received', 'received_date', { type:'date' })}
                {field('Date deposited (if done)', 'deposited_date', { type:'date' })}
              </div>
            </>) : (<>
              {field('Pay to', 'payee', { placeholder:'Who receives the money' })}
              {field('Date paid', 'date', { type:'date' })}
            </>)}
            <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:14 }}>
              {field('Purpose', 'purpose', { placeholder:'e.g. Deposit per APS clause 2' })}
              {field('Cheque / EFT / ref #', 'reference_number', { mono:true })}
            </div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowEntry(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={submitEntry}>Send for approval</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account settings */}
      <Dialog open={showAccount} onOpenChange={setShowAccount}>
        <DialogContent style={{ maxWidth:480, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Real Estate Trust Account</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>One account at an Ontario institution. The broker of record, or the alternate when they’re away, authorizes every trust transaction.</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>Institution</Over><input style={input} value={acct.institution || ''} onChange={e => setAcct({ ...acct, institution: e.target.value })} placeholder="e.g. RBC Royal Bank"/></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
              <div><Over color={p.ink3}>Transit</Over><input style={{ ...input, fontFamily:F.mono }} value={acct.transit_number || ''} onChange={e => setAcct({ ...acct, transit_number: e.target.value })}/></div>
              <div><Over color={p.ink3}>Acct last 4</Over><input style={{ ...input, fontFamily:F.mono }} value={acct.account_last4 || ''} onChange={e => setAcct({ ...acct, account_last4: e.target.value })}/></div>
              <div><Over color={p.ink3}>Opened</Over><input type="date" style={input} value={acct.opened_on || ''} onChange={e => setAcct({ ...acct, opened_on: e.target.value })}/></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><Over color={p.ink3}>Broker of record</Over>
                <select style={input} value={acct.broker_of_record_id || ''} onChange={e => setAcct({ ...acct, broker_of_record_id: e.target.value })}>
                  <option value="">Choose…</option>{members.map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
                </select></div>
              <div><Over color={p.ink3}>Alternate</Over>
                <select style={input} value={acct.alternate_broker_id || ''} onChange={e => setAcct({ ...acct, alternate_broker_id: e.target.value })}>
                  <option value="">None</option>{members.map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
                </select></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, alignItems:'end' }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:p.ink2 }}>
                <input type="checkbox" checked={!!acct.interest_bearing} onChange={e => setAcct({ ...acct, interest_bearing: e.target.checked })}/> Interest-bearing
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:p.ink2 }}>
                <input type="checkbox" checked={!!acct.variable_rate} disabled={!acct.interest_bearing} onChange={e => setAcct({ ...acct, variable_rate: e.target.checked })}/> Variable rate
              </label>
              <div><Over color={p.ink3}>Rate (%)</Over><input type="number" step="0.01" style={{ ...input, fontFamily:F.mono }} value={acct.interest_rate ?? ''} onChange={e => setAcct({ ...acct, interest_rate: e.target.value })} disabled={!acct.interest_bearing}/></div>
            </div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowAccount(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={saveAccount}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
